import { Outlet, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { AppShell, Group, Text, Stack, Avatar, Menu, ActionIcon, Tooltip, Center, Loader, Badge } from '@mantine/core';
import {
  IconPlane,
  IconDashboard,
  IconUpload,
  IconTable,
  IconCalendar,
  IconChartBar,
  IconBriefcase,
  IconLogout,
} from '@tabler/icons-react';
import { useAuth } from './useAuth';
import { usePermissions } from '../../hooks/usePermissions';
import userService from '../../services/userService';

const PROFILE_LABELS = {
  manager: 'Administrador',
  user: 'Usuario',
  viewer: 'Visor',
};

export default function DCLayout() {
  const { logout, user, isReady, isAuthenticated } = useAuth();
  const { canUploadFiles } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();

  const activePath = location.pathname;
  
  // Información del usuario (sin lógica condicional)
  const userName = user?.name || 'Usuario';
  const userEmail = user?.email || '';
  const userProfile = userService.getProfile(userEmail);
  const profileLabel = userProfile ? PROFILE_LABELS[userProfile] : 'Sin perfil';

  // Esperar a que la autenticación esté lista
  const isReadyToRender = isReady || isAuthenticated;

  // Protección de ruta /dc/carga
  if (activePath === '/dc/carga' && !canUploadFiles) {
    return <Navigate to="/dc" replace />;
  }

  const handleLogout = () => {
    logout();
  };

  return (
    <AppShell
      header={{ height: 64 }}
      navbar={{ width: 70, breakpoint: 'sm' }}
      padding="md"
    >
      {/* Header */}
      <AppShell.Header style={{ backgroundColor: '#020C41', borderBottom: '3px solid #FA0073' }}>
        <Group h="100%" px="md" justify="space-between">
          <Group gap="sm">
            <IconPlane size={28} color="white" />
            <Text fw={700} size="lg" c="white">Aeroméxico Cargo</Text>
          </Group>
          <Menu shadow="md" width={200}>
            <Menu.Target>
              <Avatar color="pink" radius="xl" style={{ cursor: 'pointer' }}>
                {userName
                  .split(' ')
                  .filter(Boolean)
                  .slice(0, 2)
                  .map((w) => w[0].toUpperCase())
                  .join('')}
              </Avatar>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>{userName}</Menu.Label>
              <Menu.Label>{userEmail}</Menu.Label>
              <Menu.Divider />
              <Menu.Item disabled>
                <Group gap="xs">
                  <Text size="sm" c="dimmed">Perfil</Text>
                  <Badge size="sm" color="#030D75" variant="light">{profileLabel}</Badge>
                </Group>
              </Menu.Item>
              <Menu.Divider />
              <Menu.Item leftSection={<IconLogout size={14} />} onClick={handleLogout} color="red">
                Cerrar sesión
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </AppShell.Header>

      {/* Navbar — Solo iconos */}
      <AppShell.Navbar p="xs" style={{ backgroundColor: '#ffffff', borderRight: '1px solid #e0e0e0', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <Stack gap="md" align="center" justify="center" style={{ flex: 1 }}>
          <Tooltip label="Dashboard" position="right" withArrow>
            <ActionIcon size="xl" variant={activePath === '/dc' ? 'light' : 'subtle'} color={activePath === '/dc' ? '#FA0073' : '#020C41'} onClick={() => navigate('/dc')}>
              <IconDashboard size={24} />
            </ActionIcon>
          </Tooltip>
          {canUploadFiles && (
            <Tooltip label="Carga de Archivos" position="right" withArrow>
              <ActionIcon size="xl" variant={activePath === '/dc/carga' ? 'light' : 'subtle'} color={activePath === '/dc/carga' ? '#FA0073' : '#020C41'} onClick={() => navigate('/dc/carga')}>
                <IconUpload size={24} />
              </ActionIcon>
            </Tooltip>
          )}
          <Tooltip label="CAP TD" position="right" withArrow>
            <ActionIcon size="xl" variant={activePath === '/dc/cap-td' ? 'light' : 'subtle'} color={activePath === '/dc/cap-td' ? '#FA0073' : '#020C41'} onClick={() => navigate('/dc/cap-td')}>
              <IconTable size={24} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="TD ITI" position="right" withArrow>
            <ActionIcon size="xl" variant={activePath === '/dc/td-iti' ? 'light' : 'subtle'} color={activePath === '/dc/td-iti' ? '#FA0073' : '#020C41'} onClick={() => navigate('/dc/td-iti')}>
              <IconCalendar size={24} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="FOC" position="right" withArrow>
            <ActionIcon size="xl" variant={activePath === '/dc/fco' ? 'light' : 'subtle'} color={activePath === '/dc/fco' ? '#FA0073' : '#020C41'} onClick={() => navigate('/dc/fco')}>
              <IconChartBar size={24} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Consulta Comercial" position="right" withArrow>
            <ActionIcon size="xl" variant={activePath === '/dc/comercial' ? 'light' : 'subtle'} color={activePath === '/dc/comercial' ? '#FA0073' : '#020C41'} onClick={() => navigate('/dc/comercial')}>
              <IconBriefcase size={24} />
            </ActionIcon>
          </Tooltip>
        </Stack>
      </AppShell.Navbar>

      {/* Main Content */}
      <AppShell.Main style={{ backgroundColor: '#F2F2F0' }}>
        {isReadyToRender ? <Outlet /> : (
          <Center style={{ height: '100%' }}>
            <Loader color="#FA0073" type="dots" size={60} />
          </Center>
        )}
      </AppShell.Main>
    </AppShell>
  );
}
